import { KeyboardArrowDown } from '@mui/icons-material';
import {
  type CategoryResponse,
  v1HeadlinesAddSiteCategoryCreate,
} from '@on3/api';
import { Popover } from '@on3/ui-lib/components/Popover';
import { externalApi, useAuth, useToast } from '@on3/ui-lib/index';
import { type EditorSite } from '@on3/ui-lib/store/editorSites';
import { useRouter } from 'next/navigation';
import { useRef } from 'react';

import styles from './EditorSlot.module.scss';

interface EditorSlotProps {
  articleKey?: number | null;
  categories?: CategoryResponse[] | undefined;
  editorSites?: EditorSite[];
}

export const EditorSlot = ({
  articleKey,
  categories,
  editorSites,
}: EditorSlotProps) => {
  const { notify } = useToast();
  const router = useRouter();
  const { user } = useAuth();
  const slotPopoverRef = useRef<HTMLButtonElement>(null);

  if (!articleKey || !editorSites?.length || !user?.rg) return null;

  const handleSlot = (site: EditorSite) => async () => {
    try {
      notify(`Slotting article to ${site.site_name}...`, { type: 'info' });

      await v1HeadlinesAddSiteCategoryCreate(
        externalApi,
        articleKey as number,
        site.site_key,
      );

      notify('Article slotted successfully!', { type: 'success' });
      router.refresh();
    } catch (error) {
      console.error('Error slotted article:', error);
      notify(
        'Failed to slot article. If the problem persists, please contact support.',
        { type: 'error' },
      );
    }
  };

  return editorSites.length > 1 ? (
    <Popover className={styles.slotTool}>
      {({ isOpen, setIsOpen, Wrapper }) => (
        <>
          <button
            className={styles.slotButton}
            onClick={() => setIsOpen(!isOpen)}
            ref={slotPopoverRef}
            type="button"
          >
            Slot to Site
            <KeyboardArrowDown />
          </button>
          {isOpen && (
            <Wrapper className={styles.popover} controlRef={slotPopoverRef}>
              {editorSites.map((site) => (
                <button
                  disabled={
                    categories?.findIndex(
                      (cat) => cat.key === site.category_key,
                    ) !== -1
                  }
                  key={site.role}
                  onClick={handleSlot(site)}
                  type="button"
                >
                  {site.site_name}
                </button>
              ))}
            </Wrapper>
          )}
        </>
      )}
    </Popover>
  ) : (
    <button
      className={styles.slotButton}
      disabled={
        categories?.findIndex(
          (cat) => cat.key === editorSites[0].category_key,
        ) !== -1
      }
      onClick={handleSlot(editorSites[0])}
      type="button"
    >
      {categories?.findIndex(
        (cat) => cat.key === editorSites[0].category_key,
      ) !== -1
        ? `Slotted to ${editorSites[0].site_name}`
        : `Slot to ${editorSites[0].site_name}`}
    </button>
  );
};
